import { Block, NetworkStats, NetworkSnapshot, FeeEstimates, MempoolInfo } from '../models';
import { SimpleRateLimiter } from './RateLimiter';

export interface APIEndpoints {
  mempool: {
    baseUrl: string;
    blocks: string;
    fees: string;
    hashrate: string;
    mempool: string;
    stats: string;
  };
}

export class DataAggregator {
  private rateLimiter = new SimpleRateLimiter();
  private endpoints: APIEndpoints;
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

  constructor(endpoints: APIEndpoints) {
    this.endpoints = endpoints;
  }

  async fetchLatestBlocks(count: number = 10): Promise<Block[]> {
    return this.rateLimiter.throttle(async () => {
      const cacheKey = `blocks-${count}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;

      const url = `${this.endpoints.mempool.baseUrl}${this.endpoints.mempool.blocks}?count=${count}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch blocks: ${response.statusText}`);
      }

      const blocksData = await response.json();
      const blocks: Block[] = blocksData.map((blockData: any) => this.parseBlockFromMempool(blockData));
      
      this.setCache(cacheKey, blocks, 30000); // 30 second cache
      return blocks;
    }, 100); // 100ms throttle
  }

  async fetchNetworkStats(): Promise<NetworkStats> {
    return this.rateLimiter.throttle(async () => {
      const cacheKey = 'network-stats';
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;

      const [hashrate, fees, mempool, tipHeight] = await Promise.all([
        this.fetchHashrate(),
        this.fetchFeeEstimates(),
        this.fetchMempoolInfo(),
        this.fetchTipHeight() // Add current block height
      ]);

      const stats: NetworkStats = {
        hashrate: hashrate.hashrate,
        difficulty: hashrate.difficulty,
        nextAdjustment: hashrate.nextAdjustment,
        mempoolSize: mempool.size,
        mempoolTxCount: mempool.txCount,
        feeEstimates: fees,
        mempool,
        currentHeight: tipHeight // Include current height
      };

      this.setCache(cacheKey, stats, 30000); // 30 second cache
      return stats;
    }, 100);
  }

  async fetchFeeEstimates(): Promise<FeeEstimates> {
    const url = `${this.endpoints.mempool.baseUrl}${this.endpoints.mempool.fees}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch fee estimates: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Validate and sanitize fee data
    const validateFee = (fee: any, fallback: number = 1): number => {
      const parsed = Number(fee);
      return isNaN(parsed) || parsed < 1 ? fallback : parsed;
    };
    
    return {
      fastestFee: validateFee(data.fastestFee, 1),
      halfHourFee: validateFee(data.halfHourFee, 1),
      hourFee: validateFee(data.hourFee, 1),
      economyFee: validateFee(data.economyFee, 1),
      minimumFee: validateFee(data.minimumFee, 1)
    };
  }

  async fetchMempoolInfo(): Promise<MempoolInfo> {
    const url = `${this.endpoints.mempool.baseUrl}${this.endpoints.mempool.mempool}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch mempool info: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      size: data.vsize / (1024 * 1024), // Convert to MB
      txCount: data.count,
      usage: data.usage || 0,
      totalFee: data.total_fee || 0
    };
  }

  private async fetchHashrate(): Promise<{
    hashrate: number;
    difficulty: number;
    nextAdjustment: { blocksRemaining: number; estimatedTime: number; estimatedChange: number; };
  }> {
    // Use 3-month period for stable hashrate data
    const url = `${this.endpoints.mempool.baseUrl}${this.endpoints.mempool.hashrate}/3m`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch hashrate: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Parse according to Mempool.space API response format
    return {
      hashrate: data.currentHashrate || 0, // Current real-time hashrate
      difficulty: data.currentDifficulty || 0, // Current real-time difficulty
      nextAdjustment: {
        // Note: Mempool.space hashrate API doesn't include adjustment info
        // This would need to come from a different endpoint
        blocksRemaining: 0,
        estimatedTime: 0,
        estimatedChange: 0
      }
    };
  }

  private async fetchTipHeight(): Promise<number> {
    const url = `${this.endpoints.mempool.baseUrl}/api/blocks/tip/height`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch tip height: ${response.statusText}`);
    }

    const height = await response.json();
    return Number(height) || 0;
  }

  async getNetworkSnapshot(): Promise<NetworkSnapshot> {
    return this.rateLimiter.backoff(async () => {
      const [blocks, networkStats] = await Promise.all([
        this.fetchLatestBlocks(10),
        this.fetchNetworkStats()
      ]);

      return {
        blocks,
        mempool: networkStats.mempool,
        network: {
          hashrate: networkStats.hashrate,
          difficulty: networkStats.difficulty,
          nextAdjustment: networkStats.nextAdjustment,
          mempoolSize: networkStats.mempoolSize,
          mempoolTxCount: networkStats.mempoolTxCount,
          feeEstimates: networkStats.feeEstimates,
          currentHeight: networkStats.currentHeight
        },
        timestamp: Date.now()
      };
    }, 3);
  }

  private parseBlockFromMempool(data: any): Block {
    // Validate and sanitize block data
    const validateNumber = (value: any, fallback: number = 0): number => {
      const parsed = Number(value);
      return isNaN(parsed) ? fallback : parsed;
    };

    const validateString = (value: any, fallback: string = ''): string => {
      return typeof value === 'string' ? value : fallback;
    };

    const validateTimestamp = (value: any): number => {
      const parsed = Number(value);
      if (isNaN(parsed)) return Date.now();
      // Convert from seconds to milliseconds if needed
      return parsed < 1e12 ? parsed * 1000 : parsed;
    };

    return {
      height: validateNumber(data.height, 0),
      hash: validateString(data.id, ''),
      timestamp: validateTimestamp(data.timestamp),
      transactionCount: validateNumber(data.tx_count, 0),
      size: validateNumber(data.size, 0),
      weight: validateNumber(data.weight, 0),
      minerPool: data.extras?.pool?.name,
      totalFees: validateNumber(data.extras?.totalFees, 0),
      subsidyReward: validateNumber(data.extras?.reward, 0),
      difficulty: validateNumber(data.difficulty, 0),
      nonce: validateNumber(data.nonce, 0),
      merkleRoot: validateString(data.merkle_root, ''),
      previousBlockHash: validateString(data.previousblockhash, '')
    };
  }

  private getFromCache(key: string): any {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCache(key: string, data: any, ttl: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  clearCache(): void {
    this.cache.clear();
  }

  // Health check method
  async healthCheck(): Promise<{ mempool: boolean; overall: boolean }> {
    try {
      const testUrl = `${this.endpoints.mempool.baseUrl}/api/blocks/tip/height`;
      const response = await fetch(testUrl);
      const mempoolHealthy = response.ok;
      
      return {
        mempool: mempoolHealthy,
        overall: mempoolHealthy
      };
    } catch {
      return {
        mempool: false,
        overall: false
      };
    }
  }
}